<template>
  <v-container fluid class="enquiries-container pa-6">
    <!-- Header Section -->
    <div class="page-header mb-6">
      <div class="d-flex align-center justify-space-between flex-wrap gap-3">
        <div>
          <div class="overline-label">Dashboard</div>
          <h1 class="page-title">Website Enquiries</h1>
          <p class="page-subtitle">Manage and respond to incoming enquiries</p>
        </div>
        <v-btn
          color="primary"
          elevation="0"
          class="refresh-btn"
          @click="loadEnquiries"
          :loading="loading"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Stats Row -->
    <v-row class="mb-6" dense>
      <v-col cols="12" sm="4">
        <div class="stat-card stat-card--total">
          <div class="stat-icon"><v-icon>mdi-email-multiple-outline</v-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ enquiries.length }}</div>
            <div class="stat-label">Total Enquiries</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card stat-card--today">
          <div class="stat-icon"><v-icon>mdi-calendar-today</v-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ todayCount }}</div>
            <div class="stat-label">Today</div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card stat-card--week">
          <div class="stat-icon"><v-icon>mdi-chart-line</v-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ weekCount }}</div>
            <div class="stat-label">This Week</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Main Table Card -->
    <v-card class="table-card" elevation="0">
      <!-- Toolbar -->
      <div class="table-toolbar">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
          <!-- Left: count + filter chip -->
          <div class="d-flex align-center flex-wrap" style="gap: 10px;">
            <div class="result-count">
              <span class="count-badge">{{ filteredCount }}</span>
              <span class="count-text">enquiries found</span>
            </div>

            <!-- Company Email Filter -->
            <div
              class="filter-chip"
              :class="{ 'filter-chip--active': companyEmailOnly }"
              @click="companyEmailOnly = !companyEmailOnly"
            >
              <div class="filter-chip-check">
                <v-icon x-small>{{ companyEmailOnly ? 'mdi-check' : 'mdi-domain' }}</v-icon>
              </div>
              <span>Company emails only</span>
              <span class="filter-chip-count">{{ companyEmailCount }}</span>
            </div>
          </div>

          <!-- Right: search -->
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search by name, email, message..."
            single-line
            hide-details
            outlined
            dense
            clearable
            class="search-field"
          />
        </div>
      </div>

      <v-divider />

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="filteredEnquiries"
        :search="search"
        :items-per-page="10"
        :footer-props="footerProps"
        class="enquiries-table"
        @pagination="onPagination"
      >
        <!-- Name -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <div class="avatar-circle mr-3">{{ getInitials(item.name) }}</div>
            <span class="name-text">{{ item.name }}</span>
          </div>
        </template>

        <!-- Email -->
        <template v-slot:item.email="{ item }">
          <div class="d-flex align-center" style="gap: 6px;">
            <a :href="`mailto:${item.email}`" class="email-link">
              <v-icon x-small class="mr-1" style="color: inherit;">mdi-email-outline</v-icon>
              {{ item.email }}
            </a>
            <span
              v-if="!isPersonalEmail(item.email)"
              class="email-badge email-badge--company"
            >
              <v-icon style="font-size: 9px; color: inherit;">mdi-domain</v-icon>
              Company
            </span>
            <span v-else class="email-badge email-badge--personal">
              <v-icon style="font-size: 9px; color: inherit;">mdi-account-outline</v-icon>
              Personal
            </span>
          </div>
        </template>

        <!-- Phone -->
        <template v-slot:item.phone="{ item }">
          <a :href="`tel:${item.phone}`" class="phone-link" v-if="item.phone">
            <v-icon x-small class="mr-1" style="color: inherit;">mdi-phone-outline</v-icon>
            {{ item.phone }}
          </a>
          <span v-else class="text--disabled">—</span>
        </template>

        <!-- Date -->
        <template v-slot:item.created_at="{ item }">
          <div class="date-cell">
            <div class="date-primary">{{ formatDate(item.created_at) }}</div>
            <div class="date-secondary">{{ formatTime(item.created_at) }}</div>
          </div>
        </template>

        <!-- Message -->
        <template v-slot:item.message="{ item }">
          <div class="message-cell">
            <span class="message-preview">{{ truncateMessage(item.message) }}</span>
            <v-btn
              v-if="item.message && item.message.length > 80"
              x-small text color="primary"
              class="read-more-btn ml-1"
              @click="openMessageDialog(item)"
            >Read more</v-btn>
          </div>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="empty-state py-12">
            <v-icon size="56" color="grey lighten-2">mdi-email-off-outline</v-icon>
            <div class="empty-title mt-4">No enquiries yet</div>
            <div class="empty-subtitle">When visitors submit enquiries, they'll appear here</div>
          </div>
        </template>

        <!-- No search results -->
        <template v-slot:no-results>
          <div class="empty-state py-12">
            <v-icon size="56" color="grey lighten-2">mdi-magnify-close</v-icon>
            <div class="empty-title mt-4">No results for "{{ search }}"</div>
            <div class="empty-subtitle">Try a different search term</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Message Detail Dialog -->
    <v-dialog v-model="dialog" max-width="560" content-class="message-dialog">
      <v-card class="dialog-card" v-if="selectedEnquiry">
        <div class="dialog-header">
          <div class="d-flex align-center">
            <div class="avatar-circle avatar-circle--lg mr-4">{{ getInitials(selectedEnquiry.name) }}</div>
            <div>
              <div class="dialog-name">{{ selectedEnquiry.name }}</div>
              <div class="dialog-meta">{{ formatDate(selectedEnquiry.created_at) }} · {{ formatTime(selectedEnquiry.created_at) }}</div>
            </div>
          </div>
          <v-btn icon @click="dialog = false" class="close-btn"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="dialog-contact-info">
          <a :href="`mailto:${selectedEnquiry.email}`" class="contact-chip">
            <v-icon small>mdi-email-outline</v-icon>
            {{ selectedEnquiry.email }}
          </a>
          <a v-if="selectedEnquiry.phone" :href="`tel:${selectedEnquiry.phone}`" class="contact-chip">
            <v-icon small>mdi-phone-outline</v-icon>
            {{ selectedEnquiry.phone }}
          </a>
        </div>

        <v-divider />

        <div class="dialog-body">
          <div class="message-label">
            <v-icon small class="mr-1">mdi-message-text-outline</v-icon>
            Message
          </div>
          <div class="message-full-text">{{ selectedEnquiry.message }}</div>
        </div>

        <v-divider />

        <div class="dialog-actions">
          <v-btn outlined color="primary" :href="`mailto:${selectedEnquiry.email}`" class="reply-btn">
            <v-icon left small>mdi-reply</v-icon>
            Reply via Email
          </v-btn>
          <v-btn text @click="dialog = false">Close</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import RestResource from "../../../services/dataServiceWebsite";
const service = new RestResource();

const PERSONAL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "yahoo.co.uk", "yahoo.in",
  "hotmail.com", "hotmail.co.uk", "outlook.com", "live.com",
  "icloud.com", "me.com", "mac.com", "aol.com",
  "protonmail.com", "proton.me", "mail.com",
  "ymail.com", "msn.com", "googlemail.com",
]);

export default {
  data() {
    return {
      search: "",
      loading: false,
      dialog: false,
      selectedEnquiry: null,
      filteredCount: 0,
      companyEmailOnly: false,
      headers: [
        { text: "Name",    value: "name",       sortable: true,  width: "160px" },
        { text: "Email",   value: "email",      sortable: true,  width: "260px" },
        { text: "Phone",   value: "phone",      sortable: true,  width: "150px" },
        { text: "Date",    value: "created_at", sortable: true,  width: "130px" },
        { text: "Message", value: "message",    sortable: false },
      ],
      footerProps: {
        "items-per-page-options": [10, 25, 50],
        "items-per-page-text": "Rows per page:",
      },
      enquiries: [],
    };
  },
  computed: {
    filteredEnquiries() {
      if (!this.companyEmailOnly) return this.enquiries;
      return this.enquiries.filter((e) => !this.isPersonalEmail(e.email));
    },
    companyEmailCount() {
      return this.enquiries.filter((e) => !this.isPersonalEmail(e.email)).length;
    },
    todayCount() {
      const today = new Date().toDateString();
      return this.enquiries.filter(
        (e) => new Date(e.created_at).toDateString() === today
      ).length;
    },
    weekCount() {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return this.enquiries.filter(
        (e) => new Date(e.created_at) >= oneWeekAgo
      ).length;
    },
  },
  mounted() {
    this.loadEnquiries();
  },
  methods: {
    loadEnquiries() {
      this.loading = true;
      this.$setLoader();
      service.getEnquiry().then((r) => {
        this.enquiries = r.data.enquiries;
        this.filteredCount = this.enquiries.length;
        this.loading = false;
        this.$disableLoader();
      });
    },
    isPersonalEmail(email) {
      if (!email) return false;
      const domain = email.split("@")[1]?.toLowerCase();
      return PERSONAL_DOMAINS.has(domain);
    },
    getInitials(name) {
      if (!name) return "?";
      return name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      });
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleTimeString("en-GB", {
        hour: "2-digit", minute: "2-digit",
      });
    },
    truncateMessage(msg) {
      if (!msg) return "—";
      return msg.length > 80 ? msg.substring(0, 80) + "…" : msg;
    },
    openMessageDialog(item) {
      this.selectedEnquiry = item;
      this.dialog = true;
    },
    onPagination(pagination) {
      this.filteredCount = pagination.itemsLength;
    },
  },
};
</script>

<style scoped>
/* ─── Base ──────────────────────────────────────────── */
.enquiries-container {
  background: #f4f6fa;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
}

/* ─── Page Header ────────────────────────────────────── */
.overline-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b7a99;
  margin-bottom: 4px;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin: 0 0 4px;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7a99;
  margin: 0;
}
.refresh-btn {
  border-radius: 10px !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  font-size: 14px;
  padding: 0 20px !important;
  height: 40px !important;
  background: #2563eb !important;
}

/* ─── Stat Cards ─────────────────────────────────────── */
.stat-card {
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
}
.stat-card:hover { transform: translateY(-2px); }
.stat-card--total {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(37,99,235,0.28);
}
.stat-card--today {
  background: linear-gradient(135deg, #064e3b 0%, #059669 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(5,150,105,0.28);
}
.stat-card--week {
  background: linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 8px 24px rgba(124,58,237,0.28);
}
.stat-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.18);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.stat-icon .v-icon { color: white !important; font-size: 22px; }
.stat-value { font-size: 30px; font-weight: 800; line-height: 1; letter-spacing: -1px; }
.stat-label { font-size: 12px; opacity: 0.82; font-weight: 500; margin-top: 3px; letter-spacing: 0.03em; }

/* ─── Table Card ─────────────────────────────────────── */
.table-card {
  border-radius: 16px !important;
  border: 1px solid #e5e9f2 !important;
  background: #ffffff !important;
  overflow: hidden;
}

/* ─── Toolbar ────────────────────────────────────────── */
.table-toolbar { padding: 16px 20px; }
.result-count { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.count-badge {
  background: #eff6ff; color: #2563eb;
  font-size: 13px; font-weight: 700;
  padding: 2px 10px; border-radius: 20px;
}
.count-text { font-size: 13px; color: #6b7a99; }
.search-field { max-width: 300px; border-radius: 10px !important; }
.search-field >>> .v-input__control .v-input__slot { border-radius: 10px !important; }

/* ─── Filter Chip ────────────────────────────────────── */
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px 6px 8px;
  border-radius: 20px;
  border: 1.5px solid #e5e9f2;
  background: #f8faff;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: #6b7a99;
  transition: all 0.18s ease;
  user-select: none;
}
.filter-chip:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #2563eb;
}
.filter-chip--active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}
.filter-chip-check {
  width: 20px; height: 20px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: #e5e9f2;
  transition: background 0.18s;
}
.filter-chip--active .filter-chip-check {
  background: #2563eb;
}
.filter-chip--active .filter-chip-check .v-icon {
  color: white !important;
}
.filter-chip-count {
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 10px;
  margin-left: 2px;
}
.filter-chip--active .filter-chip-count {
  background: #2563eb;
  color: white;
}

/* ─── Email Badges ───────────────────────────────────── */
.email-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}
.email-badge--company {
  background: #dcfce7;
  color: #15803d;
}
.email-badge--personal {
  background: #f3f4f6;
  color: #9ca3af;
}

/* ─── Table Overrides ────────────────────────────────── */
.enquiries-table >>> .v-data-table__wrapper { overflow-x: auto; }
.enquiries-table >>> thead tr th {
  background: #f8faff !important;
  color: #6b7a99 !important;
  font-size: 11.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em !important;
  text-transform: uppercase;
  border-bottom: 1px solid #e5e9f2 !important;
  padding: 12px 16px !important;
  white-space: nowrap;
}
.enquiries-table >>> tbody tr { transition: background 0.15s; }
.enquiries-table >>> tbody tr:hover { background: #f8faff !important; }
.enquiries-table >>> tbody tr td {
  padding: 14px 16px !important;
  border-bottom: 1px solid #f0f2f7 !important;
  font-size: 13.5px;
  color: #374151;
  vertical-align: middle;
}

/* ─── Avatar ─────────────────────────────────────────── */
.avatar-circle {
  width: 34px; height: 34px; min-width: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1d4ed8;
  font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  letter-spacing: 0.03em;
}
.avatar-circle--lg {
  width: 46px; height: 46px; min-width: 46px; font-size: 15px;
}
.name-text { font-weight: 600; color: #111827; font-size: 13.5px; }

/* ─── Links ──────────────────────────────────────────── */
.email-link, .phone-link {
  color: #2563eb; text-decoration: none;
  font-size: 13px;
  display: inline-flex; align-items: center;
  transition: color 0.15s;
}
.email-link:hover, .phone-link:hover { color: #1d4ed8; text-decoration: underline; }

/* ─── Date ───────────────────────────────────────────── */
.date-cell { line-height: 1.4; }
.date-primary { font-size: 13px; font-weight: 500; color: #374151; white-space: nowrap; }
.date-secondary { font-size: 11.5px; color: #9ca3af; }

/* ─── Message ────────────────────────────────────────── */
.message-cell { display: flex; align-items: baseline; flex-wrap: wrap; gap: 2px; }
.message-preview { color: #6b7a99; font-size: 13px; line-height: 1.5; }
.read-more-btn {
  font-size: 12px !important; font-weight: 600 !important;
  text-transform: none !important; letter-spacing: 0 !important;
  padding: 0 4px !important; min-width: 0 !important; height: auto !important;
}

/* ─── Empty State ────────────────────────────────────── */
.empty-state { text-align: center; }
.empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.empty-subtitle { font-size: 13px; color: #9ca3af; }

/* ─── Dialog ─────────────────────────────────────────── */
.message-dialog .v-dialog { border-radius: 18px !important; }
.dialog-card { border-radius: 18px !important; overflow: hidden; }
.dialog-header {
  padding: 24px 24px 16px;
  display: flex; align-items: flex-start; justify-content: space-between;
}
.dialog-name { font-size: 18px; font-weight: 700; color: #111827; }
.dialog-meta { font-size: 12.5px; color: #9ca3af; margin-top: 2px; }
.close-btn { margin-top: -4px; margin-right: -8px; }
.dialog-contact-info { padding: 0 24px 16px; display: flex; flex-wrap: wrap; gap: 8px; }
.contact-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f4f6fa; color: #374151; text-decoration: none;
  font-size: 13px; padding: 6px 14px; border-radius: 20px;
  border: 1px solid #e5e9f2; transition: background 0.15s, border-color 0.15s;
}
.contact-chip:hover { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.dialog-body { padding: 20px 24px; }
.message-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: #9ca3af; margin-bottom: 12px;
  display: flex; align-items: center;
}
.message-full-text {
  font-size: 14.5px; line-height: 1.75; color: #374151;
  background: #f8faff; border-radius: 12px; padding: 16px 18px;
  border: 1px solid #e5e9f2; white-space: pre-wrap; word-break: break-word;
  max-height: 320px; overflow-y: auto;
}
.dialog-actions {
  padding: 14px 20px; display: flex; align-items: center; justify-content: space-between;
}
.reply-btn {
  border-radius: 10px !important; font-weight: 600 !important;
  text-transform: none !important; letter-spacing: 0 !important;
}
</style>